import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  UserPlus, 
  Mail, 
  Shield, 
  Heart, 
  Users, 
  Clock, 
  Copy, 
  Check, 
  X, 
  MoreVertical,
  Trash2,
  ExternalLink,
  Send,
  Link2,
  RefreshCw,
  LinkIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { WalletInvitation, WalletAdministrator, WalletBeneficiary, Wallet } from "@shared/schema";

interface WalletCollaborationProps {
  walletId: string;
  wallet: Wallet;
  isOwner: boolean;
}

const inviteFormSchema = z.object({
  emails: z.string().min(1, "Please enter at least one email address"),
  inviteType: z.enum(["admin", "beneficiary", "contributor"]),
  role: z.string().optional(),
  message: z.string().max(500, "Message must be less than 500 characters").optional(),
  beneficiaryFirstName: z.string().optional(),
  beneficiaryLastName: z.string().optional(),
  beneficiaryRelationship: z.string().optional(),
  beneficiarySpendingLimit: z.number().optional(),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

function formatDate(date: Date | string | null): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function InviteDialog({ 
  walletId, 
  walletType,
  onSuccess 
}: { 
  walletId: string; 
  walletType: string;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<InviteFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      emails: "",
      inviteType: "contributor",
      role: "contributor",
      message: "",
      beneficiaryFirstName: "",
      beneficiaryLastName: "",
      beneficiaryRelationship: "",
    },
  });
  
  const inviteType = form.watch("inviteType");
  
  const createInvitation = useMutation({
    mutationFn: async (data: InviteFormValues) => {
      const emailList = data.emails
        .split(/[\n,]+/)
        .map(e => e.trim())
        .filter(e => e.length > 0);
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emailList.filter(e => !emailRegex.test(e));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email(s): ${invalidEmails.join(", ")}`);
      }
      if (emailList.length > 20) {
        throw new Error("Maximum 20 emails allowed per batch");
      }
      
      if (emailList.length > 1) {
        return await apiRequest("POST", `/api/wallets/${walletId}/bulk-invite`, {
          emails: emailList,
          inviteType: data.inviteType,
          role: data.role,
          message: data.message,
        });
      } else {
        return await apiRequest("POST", `/api/wallets/${walletId}/invitations`, {
          email: emailList[0],
          inviteType: data.inviteType,
          role: data.role,
          message: data.message,
          beneficiaryFirstName: data.beneficiaryFirstName,
          beneficiaryLastName: data.beneficiaryLastName,
          beneficiaryRelationship: data.beneficiaryRelationship,
          beneficiarySpendingLimit: data.beneficiarySpendingLimit,
        });
      }
    },
    onSuccess: (_data, variables) => {
      const emailList = variables.emails
        .split(/[\n,]+/)
        .map(e => e.trim())
        .filter(e => e.length > 0);
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
      toast({
        title: emailList.length > 1 ? `${emailList.length} invitations sent` : "Invitation sent",
        description: emailList.length > 1 
          ? `Email invitations have been sent to ${emailList.length} recipients.`
          : "An email invitation has been sent.",
      });
      form.reset();
      setOpen(false);
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const onSubmit = (data: InviteFormValues) => {
    createInvitation.mutate(data);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="button-invite-collaborator">
          <UserPlus className="h-4 w-4 mr-2" />
          Invite
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Collaborator
          </DialogTitle>
          <DialogDescription>
            Invite someone to collaborate on this wallet
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="emails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Addresses</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={"collaborator1@example.com\ncollaborator2@example.com"}
                      className="resize-none min-h-[80px]"
                      {...field} 
                      data-testid="input-invite-emails"
                    />
                  </FormControl>
                  <FormDescription>
                    Enter one email per line or separate with commas
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="inviteType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-invite-type">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="contributor">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>Contributor - Can view and contribute</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          <span>Admin - Can manage wallet</span>
                        </div>
                      </SelectItem>
                      {walletType !== "individual" && (
                        <SelectItem value="beneficiary">
                          <div className="flex items-center gap-2">
                            <Heart className="h-4 w-4" />
                            <span>Beneficiary - Can receive funds</span>
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {inviteType === "contributor" && "Can view wallet and make contributions"}
                    {inviteType === "admin" && "Full access to manage wallet settings and invitations"}
                    {inviteType === "beneficiary" && "Can receive payments from this wallet"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {inviteType === "beneficiary" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="beneficiaryFirstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="First name" 
                            {...field} 
                            data-testid="input-beneficiary-first-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="beneficiaryLastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Last name" 
                            {...field} 
                            data-testid="input-beneficiary-last-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="beneficiaryRelationship"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Relationship</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-beneficiary-relationship">
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="spouse">Spouse</SelectItem>
                          <SelectItem value="child">Child</SelectItem>
                          <SelectItem value="parent">Parent</SelectItem>
                          <SelectItem value="sibling">Sibling</SelectItem>
                          <SelectItem value="grandparent">Grandparent</SelectItem>
                          <SelectItem value="community_member">Community Member</SelectItem>
                          <SelectItem value="employee">Employee</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Personal Message (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add a personal note to the invitation..." 
                      className="resize-none"
                      {...field} 
                      data-testid="input-invite-message"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createInvitation.isPending}
                data-testid="button-send-invitation"
              >
                {createInvitation.isPending ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function ShareLinkPanel({ 
  walletId, 
  wallet 
}: { 
  walletId: string; 
  wallet: Wallet;
}) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const generateTokenMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/wallets/${walletId}/join-token`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/wallets', walletId] });
      queryClient.invalidateQueries({ queryKey: ['/api/wallet'] });
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
      toast({
        title: "Invite link generated",
        description: "Anyone with this link can request to join the wallet.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to generate invite link",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const disableTokenMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", `/api/wallets/${walletId}/join-token`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/wallets', walletId] });
      queryClient.invalidateQueries({ queryKey: ['/api/wallet'] });
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
      toast({
        title: "Invite link disabled",
        description: "The invite link is no longer active.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to disable invite link",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const shareableUrl = wallet.joinToken 
    ? `${window.location.origin}/wallet-invite/${wallet.joinToken}` 
    : "";
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard.",
        variant: "destructive",
      });
    }
  };
  
  const hasActiveToken = wallet.joinToken && wallet.joinTokenEnabled;
  
  return (
    <div className="px-6 py-4" data-testid="panel-share-link">
      <div className="flex items-center gap-2 mb-3">
        <LinkIcon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Invite Link</span>
      </div>
      {hasActiveToken ? (
        <div className="flex items-center gap-2">
          <Input 
            readOnly 
            value={shareableUrl} 
            className="text-sm" 
            data-testid="input-share-link"
          />
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleCopy}
            data-testid="button-copy-share-link"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => disableTokenMutation.mutate()}
            disabled={disableTokenMutation.isPending}
            data-testid="button-disable-share-link"
          >
            {disableTokenMutation.isPending ? "Disabling..." : "Disable"}
          </Button>
        </div>
      ) : (
        <Button 
          variant="outline" 
          onClick={() => generateTokenMutation.mutate()}
          disabled={generateTokenMutation.isPending}
          data-testid="button-generate-share-link"
        >
          {generateTokenMutation.isPending ? (
            "Generating..."
          ) : (
            <>
              <Link2 className="h-4 w-4 mr-2" />
              Generate Invite Link
            </>
          )}
        </Button>
      )}
    </div>
  );
}

function InvitationRow({ 
  invitation,
  onRevoke,
  onResend
}: { 
  invitation: WalletInvitation;
  onRevoke: () => void;
  onResend: () => void;
}) {
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  
  const getStatusBadge = () => {
    switch (invitation.status) {
      case "pending":
        return <Badge variant="outline" className="text-amber-600 border-amber-600"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "accepted":
        return <Badge variant="outline" className="text-green-600 border-green-600"><Check className="h-3 w-3 mr-1" />Accepted</Badge>;
      case "declined":
        return <Badge variant="outline" className="text-red-600 border-red-600"><X className="h-3 w-3 mr-1" />Declined</Badge>;
      case "expired":
        return <Badge variant="secondary">Expired</Badge>;
      case "revoked":
        return <Badge variant="destructive">Revoked</Badge>;
      default:
        return <Badge variant="outline">{invitation.status}</Badge>;
    }
  };
  
  const getTypeIcon = () => {
    switch (invitation.inviteType) {
      case "admin":
        return <Shield className="h-4 w-4 text-blue-600" />;
      case "beneficiary":
        return <Heart className="h-4 w-4 text-pink-600" />;
      case "contributor":
        return <Users className="h-4 w-4 text-green-600" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };
  
  return (
    <>
      <div 
        className="flex items-center justify-between py-3 px-4 border-b last:border-b-0"
        data-testid={`row-invitation-${invitation.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            {getTypeIcon()}
          </div>
          <div>
            <p className="text-sm font-medium">{invitation.email}</p>
            <p className="text-xs text-muted-foreground">
              Invited {formatDate(invitation.createdAt)} as {invitation.inviteType}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
          {invitation.status === "pending" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={onResend}
                  data-testid={`button-resend-invitation-${invitation.id}`}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend Invitation
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setShowRevokeDialog(true)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Revoke Invitation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this invitation? {invitation.email} will no longer be able to accept it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AdministratorRow({ 
  admin,
  onRevoke,
  isOwner
}: { 
  admin: WalletAdministrator;
  onRevoke: () => void;
  isOwner: boolean;
}) {
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  
  return (
    <>
      <div 
        className="flex items-center justify-between py-3 px-4 border-b last:border-b-0"
        data-testid={`row-admin-${admin.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
            <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium">{admin.userId}</p>
            <p className="text-xs text-muted-foreground">
              {admin.role} • Added {formatDate(admin.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={admin.status === "active" ? "default" : "secondary"}>
            {admin.status}
          </Badge>
          {isOwner && admin.status === "active" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => setShowRevokeDialog(true)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Revoke Access
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Administrator Access</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this administrator's access? They will no longer be able to manage this wallet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRevoke} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function BeneficiaryRow({ 
  beneficiary,
  onDeactivate,
  isOwner
}: { 
  beneficiary: WalletBeneficiary;
  onDeactivate: () => void;
  isOwner: boolean;
}) {
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  
  return (
    <>
      <div 
        className="flex items-center justify-between py-3 px-4 border-b last:border-b-0"
        data-testid={`row-beneficiary-${beneficiary.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900">
            <Heart className="h-4 w-4 text-pink-600 dark:text-pink-400" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {beneficiary.firstName} {beneficiary.lastName}
            </p>
            <p className="text-xs text-muted-foreground">
              {beneficiary.relationship || "Beneficiary"} • {beneficiary.email || "No email"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={beneficiary.isActive ? "default" : "secondary"}>
            {beneficiary.isActive ? "Active" : "Inactive"}
          </Badge>
          {isOwner && beneficiary.isActive && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => setShowDeactivateDialog(true)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Deactivate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Beneficiary</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {beneficiary.firstName} {beneficiary.lastName}? They will no longer be able to receive funds from this wallet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDeactivate} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function WalletCollaboration({ walletId, wallet, isOwner }: WalletCollaborationProps) {
  const { toast } = useToast();
  
  const { data: invitations, isLoading: invitationsLoading } = useQuery<WalletInvitation[]>({
    queryKey: [`/api/wallets/${walletId}/all-invitations`],
    enabled: isOwner,
  });
  
  const { data: administrators, isLoading: adminsLoading } = useQuery<WalletAdministrator[]>({
    queryKey: [`/api/wallets/${walletId}/administrators`],
  });
  
  const { data: beneficiaries, isLoading: beneficiariesLoading } = useQuery<WalletBeneficiary[]>({
    queryKey: [`/api/wallets/${walletId}/beneficiaries`],
    enabled: wallet.walletType !== "individual",
  });
  
  const revokeMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      return await apiRequest("POST", `/api/wallet-invitations/${invitationId}/revoke`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
      toast({
        title: "Invitation revoked",
        description: "The invitation has been cancelled.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const resendMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      return await apiRequest("POST", `/api/wallet-invitations/${invitationId}/resend`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
      toast({
        title: "Invitation resent",
        description: "The invitation email has been resent.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to resend invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const revokeAdminMutation = useMutation({
    mutationFn: async (adminId: string) => {
      return await apiRequest("POST", `/api/wallet-administrators/${adminId}/revoke`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/administrators`] });
      toast({
        title: "Access revoked",
        description: "Administrator access has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke access",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const deactivateBeneficiaryMutation = useMutation({
    mutationFn: async (beneficiaryId: string) => {
      return await apiRequest("POST", `/api/beneficiaries/${beneficiaryId}/deactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/beneficiaries`] });
      toast({
        title: "Beneficiary deactivated",
        description: "The beneficiary has been deactivated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to deactivate beneficiary",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const allInvitations = invitations || [];
  const activeAdmins = administrators?.filter(a => a.status === "active") || [];
  const activeBeneficiaries = beneficiaries?.filter(b => b.isActive) || [];
  
  const showShareLinkPanel = isOwner && wallet.walletType !== "individual";
  
  const handleInviteSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [`/api/wallets/${walletId}/all-invitations`] });
  };
  
  return (
    <Card data-testid="card-wallet-collaboration">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5" />
            Collaboration
          </CardTitle>
          <CardDescription>
            Manage who has access to this wallet
          </CardDescription>
        </div>
        {isOwner && (
          <InviteDialog 
            walletId={walletId} 
            walletType={wallet.walletType || "individual"} 
            onSuccess={handleInviteSuccess}
          />
        )}
      </CardHeader>
      <CardContent className="p-0">
        {showShareLinkPanel && (
          <>
            <ShareLinkPanel walletId={walletId} wallet={wallet} />
            <Separator />
          </>
        )}
        <Tabs defaultValue="team" className="w-full">
          <div className="px-6">
            <TabsList className={`w-full grid mb-4 ${isOwner && wallet.walletType !== "individual" ? "grid-cols-3" : "grid-cols-2"}`}>
              <TabsTrigger value="team" className="gap-1">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Team</span>
                {activeAdmins.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{activeAdmins.length}</Badge>
                )}
              </TabsTrigger>
              {wallet.walletType !== "individual" && (
                <TabsTrigger value="beneficiaries" className="gap-1">
                  <Heart className="h-4 w-4" />
                  <span className="hidden sm:inline">Beneficiaries</span>
                  {activeBeneficiaries.length > 0 && (
                    <Badge variant="secondary" className="ml-1">{activeBeneficiaries.length}</Badge>
                  )}
                </TabsTrigger>
              )}
              {isOwner && (
                <TabsTrigger value="invitations" className="gap-1">
                  <Mail className="h-4 w-4" />
                  <span className="hidden sm:inline">Invitations</span>
                  {allInvitations.length > 0 && (
                    <Badge variant="default" className="ml-1">{allInvitations.length}</Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          
          <TabsContent value="team" className="mt-0">
            {adminsLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activeAdmins.length > 0 ? (
              <div className="divide-y">
                {activeAdmins.map((admin) => (
                  <AdministratorRow
                    key={admin.id}
                    admin={admin}
                    onRevoke={() => revokeAdminMutation.mutate(admin.id)}
                    isOwner={isOwner}
                  />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No team members yet</p>
                {isOwner && (
                  <p className="text-sm mt-1">Invite administrators to help manage this wallet</p>
                )}
              </div>
            )}
          </TabsContent>
          
          {wallet.walletType !== "individual" && (
            <TabsContent value="beneficiaries" className="mt-0">
              {beneficiariesLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : activeBeneficiaries.length > 0 ? (
                <div className="divide-y">
                  {activeBeneficiaries.map((beneficiary) => (
                    <BeneficiaryRow
                      key={beneficiary.id}
                      beneficiary={beneficiary}
                      onDeactivate={() => deactivateBeneficiaryMutation.mutate(beneficiary.id)}
                      isOwner={isOwner}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Heart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No beneficiaries added</p>
                  {isOwner && (
                    <p className="text-sm mt-1">Add beneficiaries who can receive funds from this wallet</p>
                  )}
                </div>
              )}
            </TabsContent>
          )}
          
          {isOwner && (
            <TabsContent value="invitations" className="mt-0">
              {invitationsLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : allInvitations.length > 0 ? (
                <div className="divide-y">
                  {allInvitations.map((invitation) => (
                    <InvitationRow
                      key={invitation.id}
                      invitation={invitation}
                      onRevoke={() => revokeMutation.mutate(invitation.id)}
                      onResend={() => resendMutation.mutate(invitation.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No invitations yet</p>
                  <p className="text-sm mt-1">Send invitations to collaborate on this wallet</p>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default WalletCollaboration;
